/**
 * Shared HTTP transport plumbing for the `Http*Client` classes.
 *
 * Every live client repeated the same four steps: trim the configured
 * `baseUrl`, resolve an ID token, build the `Authorization` header via
 * `buildAuthHeaders`, and call `fetch` (or an injected `fetchImpl`) with a
 * JSON `Content-Type`. `createHttpTransport` centralizes that boilerplate so
 * each client only has to describe its own endpoints/methods and how to
 * parse the response (via `parseEnvelope` or a source-specific variant like
 * `parseGithubEnvelope`).
 */

import { buildAuthHeaders } from './authHeaders';
import type { HttpClientOptions } from './types';

export interface HttpTransport {
  /** Trimmed (no trailing slash) base URL this transport targets. */
  readonly baseUrl: string;
  /** Resolves the `Authorization` header for the configured base URL, if any. */
  authHeaders(): Promise<Record<string, string>>;
  /**
   * Issues a request against `${baseUrl}${path}`, merging a JSON
   * `Content-Type` and the resolved auth header under any headers the
   * caller passes in `init`.
   */
  fetch(path: string, init?: RequestInit): Promise<Response>;
}

/**
 * Builds an `HttpTransport`. Accepts the same `HttpClientOptions | string`
 * shape every `Http*Client` constructor already took, so call sites don't
 * change: `new HttpFooClient(options)` becomes
 * `createHttpTransport(options)` inside the constructor.
 */
export function createHttpTransport(options: HttpClientOptions | string = {}): HttpTransport {
  const normalized: HttpClientOptions = typeof options === 'string' ? { baseUrl: options } : options;
  const baseUrl = (normalized.baseUrl ?? '').replace(/\/+$/, '');
  const getIdToken = normalized.getIdToken;
  // Resolved lazily (not bound once at construction time): singleton clients
  // are constructed at module load, before test suites install
  // `vi.spyOn(global, 'fetch', ...)`. Looking up `fetch` at call time (like
  // every `Http*Client` did pre-refactor) lets that kind of global patch
  // still take effect; `fetchImpl` still wins when explicitly injected.
  const fetchImpl = normalized.fetchImpl;

  async function authHeaders(): Promise<Record<string, string>> {
    return buildAuthHeaders(baseUrl, getIdToken);
  }

  async function transportFetch(path: string, init?: RequestInit): Promise<Response> {
    const auth = await authHeaders();
    const doFetch = fetchImpl ?? fetch;
    return doFetch(`${baseUrl}${path}`, {
      ...init,
      headers: {
        // Sent unconditionally, including on GET — a few call sites (Slack
        // thread reads, settings reads) previously omitted this header on
        // GET before this shared transport existed. `apps/api`'s Express
        // JSON body parser ignores Content-Type on a bodyless request, so
        // this is not a behavior change for any real endpoint; documented
        // here since it's a real, if harmless, wire-contract difference.
        'Content-Type': 'application/json',
        ...auth,
        ...init?.headers,
      },
    });
  }

  return { baseUrl, authHeaders, fetch: transportFetch };
}
